import { AdminApiError, type AdminClient, type IntegratorDetail } from '@apx/admin-sdk';
import { icon } from '../icons.js';
import { fmtDateTime, fmtMoney, fmtNumber, fmtTime } from '../format.js';
import { confirmModal, promptModal } from '../modal.js';
import { showToast } from '../toast.js';

export interface IntegratorDetailPageDeps {
  client: AdminClient;
  integratorId: string;
  /** Permission probe from /v1/me. */
  can: (permission: string) => boolean;
  /** Tenant staff: this IS their integrator, not one of many. */
  isLocked: boolean;
}

export async function renderIntegratorDetailPage(
  root: HTMLElement,
  deps: IntegratorDetailPageDeps,
): Promise<void> {
  root.innerHTML = `
    <div class="sticky-head">
      <h1 class="page-title">Integrator detail</h1>
      <p class="page-sub" style="margin-bottom:14px">
        ${deps.isLocked ? '' : '<a href="/integrators" style="color:var(--accent-2);text-decoration:none">← Back to Integrators</a> &middot; '}<code>${escapeHtml(deps.integratorId)}</code>
      </p>
      <div id="td-tabs-mount"></div>
    </div>
    <div id="td-body"><p class="muted">Loading…</p></div>
  `;
  const bodyEl = root.querySelector<HTMLElement>('#td-body')!;
  const tabsMount = root.querySelector<HTMLElement>('#td-tabs-mount')!;

  async function refresh(): Promise<void> {
    try {
      const detail = await deps.client.fetchIntegratorDetail(deps.integratorId);
      tabsMount.innerHTML = renderTabs(detail);
      bodyEl.innerHTML = renderBody(detail);
      // renderBody replaces the lazy panels with fresh "Loading…" nodes, so the
      // lazy-load guards must reset or the panels would stay blank on re-entry.
      ratesPanelLoaded = false;
      jackpotsPanelLoaded = false;
      bindTabs();
      bindActions(detail);
    } catch (err) {
      bodyEl.innerHTML = `<p class="error">${escapeHtml(describe(err))}</p>`;
    }
  }

  function renderTabs(d: IntegratorDetail): string {
    return `
      <div class="game-tabs td-tabs" role="tablist">
        ${/* Each tab is a different capability. Offering one the principal
             cannot read only produces a FORBIDDEN banner on click. */ ''}
        <button type="button" class="game-tab" data-tab="overview" role="tab">Overview</button>
        ${
          deps.can('integrator.write')
            ? '<button type="button" class="game-tab" data-tab="security" role="tab">Security &amp; wallet</button>'
            : ''
        }
        <button type="button" class="game-tab" data-tab="rates" role="tab">Currencies</button>
        ${
          deps.can('report.financial.read')
            ? '<button type="button" class="game-tab" data-tab="jackpots" role="tab">Jackpots</button>'
            : ''
        }
        ${
          deps.can('game.fairness.write')
            ? '<button type="button" class="game-tab" data-tab="autoplay" role="tab">Auto-play</button>'
            : ''
        }
        ${deps.can('audit.read') ? `
        <button type="button" class="game-tab" data-tab="activity" role="tab">
          Recent activity
          ${d.recentAudits.length || d.recentRounds.length ? `<span class="muted" style="font-size:10px;margin-left:4px">(${d.recentRounds.length + d.recentAudits.length})</span>` : ''}
        </button>` : ''}
      </div>`;
  }

  function renderBody(d: IntegratorDetail): string {
    const t = d.integrator;
    const c = d.counts;
    const k = d.kpi24h;
    const gh = (sub: string): string =>
      `/integrators/${encodeURIComponent(deps.integratorId)}/${sub}`;

    /**
     * A tile is shown only when the page it links to is actually reachable.
     *
     * Each entry carries the permission that guards its destination route (the
     * same value `SIDEBAR_GUARDS` uses), so the two cannot drift apart. Without
     * this the card both advertised figures a role is not cleared for — a
     * support account could read the integrator's 24h turnover and payout off
     * this page while the P/L report itself returned 403 — and offered links
     * that could only ever dead-end.
     */
    type Tile = [perm: string, label: string, value: number | string, color: string | undefined, sub: string];
    const tiles = (rows: ReadonlyArray<Tile>): string =>
      rows
        .filter(([perm]) => deps.can(perm))
        .map(([, label, value, color, sub]) => kpiCell(label, value, color, gh(sub)))
        .join('');

    const last24h = [
      // Round volume is activity, not money — it follows the round explorer's
      // own guard rather than the financial one.
      ...(deps.can('audit.read') ? [kpiCell('Rounds', k.rounds, undefined, gh('rounds-explorer'))] : []),
      ...(deps.can('report.financial.read')
        ? [
            kpiCell('Total bet', fmtNumber(k.totalBet), undefined, gh('reports/pl')),
            kpiCell('Total payout', fmtNumber(k.totalPayout), undefined, gh('reports/pl')),
          ]
        : []),
    ];

    return `
      <div data-panel="overview" class="td-panel">
        <div style="display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1.1fr);gap:16px;align-items:stretch">
          <div class="card" style="margin-bottom:0">
            <div class="detail-profile-head">
              <span class="detail-avatar">${escapeHtml(monogramOf(t.name || t.slug))}</span>
              <div style="min-width:0">
                <div class="detail-profile-name">${escapeHtml(t.name)}</div>
                <div class="detail-profile-sub">
                  <code>${escapeHtml(t.slug)}</code>
                  <span class="pill ${t.status === 'active' ? 'live' : 'warn'}">${t.status}</span>
                </div>
              </div>
            </div>
            <dl class="kv">
              <dt>ID</dt><dd class="detail-id-cell"><code>${t.id}</code><button type="button" class="ghost detail-copy" data-copy="${t.id}" title="Copy ID">${icon('check', { size: 13 })}</button></dd>
              <dt>Locale</dt><dd>${escapeHtml(t.defaultLocale)}</dd>
              <dt>Default fiat</dt><dd><code>${escapeHtml(t.defaultFiatCurrency)}</code></dd>
              <dt>Created</dt><dd>${fmtDateTime(t.createdAt)}</dd>
            </dl>
            <div class="row" style="margin-top:16px;flex-wrap:wrap">
              ${
                // PATCH /v1/integrators/:id enforces this server-side; offering
                // the button without it only produced a 403 after the operator
                // had already typed a new name.
                deps.can('integrator.settings.write') ? '<button id="td-rename">Rename</button>' : ''
              }
              ${deps.can('integrator.suspend') ? `<button id="td-toggle-status" class="ghost">${t.status === 'active' ? 'Suspend' : 'Reactivate'}</button>` : ''}
            </div>
          </div>
          <div class="card" style="margin-bottom:0">
            <div class="detail-card-head">
              <h2>At a glance</h2>
              <span class="muted">Live totals for this integrator. Click any tile to drill in.</span>
            </div>
            <div class="detail-stat-grid">
              ${tiles([
                ['player.read', 'Players', c.players, undefined, 'players'],
                ['api_key.write', 'API keys', c.apiKeys, undefined, 'api-keys'],
                ['webhook.write', 'Webhooks', c.webhooks, undefined, 'webhooks'],
                ['game.config.write', 'Pending approvals', c.pendingApprovals, c.pendingApprovals > 0 ? 'var(--warn)' : undefined, 'approvals'],
                ['report.financial.read', 'Unresolved drift', c.unresolvedDrift ?? 0, (c.unresolvedDrift ?? 0) > 0 ? 'var(--warn)' : undefined, 'wallet-drift'],
                ['report.financial.read', 'Failed wallet calls', c.failedWalletCalls ?? 0, (c.failedWalletCalls ?? 0) > 0 ? 'var(--warn)' : undefined, 'wallet-ledger'],
              ])}
            </div>
            ${
              // The whole block goes when nothing in it survives the gate,
              // rather than leaving a "Last 24h" heading over empty space.
              last24h.length === 0
                ? ''
                : `<div class="detail-subhead muted">Last 24h</div>
                   <div class="detail-stat-grid detail-stat-grid-3">${last24h.join('')}</div>`
            }
          </div>
        </div>
      </div>

      <div data-panel="security" class="td-panel">
        <p class="muted sec-intro">
          Integrators are gated by source IP + iframe origin + currency whitelist.
          Wallet endpoints are called outbound from apx with an HMAC-signed body.
        </p>

        <div class="card sec-card">
          <div class="sec-card-head">
            <h2>Wallet endpoint</h2>
            <span class="muted">Where apx sends signed debit/credit/settle calls.</span>
          </div>
          <div class="sec-grid">
            <label class="field" style="grid-column:1 / -1">
              <span class="field-label">Endpoint URL <code class="field-hint">walletBaseUrl</code></span>
              <input id="td-wallet-url" type="url" placeholder="https://api.integrator.example/apx" value="${escapeHtml(t.walletBaseUrl ?? '')}" />
            </label>
            <label class="field">
              <span class="field-label">Request timeout (ms) <code class="field-hint">walletTimeoutMs</code></span>
              <input id="td-wallet-timeout" type="number" min="100" max="10000" value="${t.walletTimeoutMs}" />
            </label>
            <div class="field">
              <span class="field-label">Signing secret <code class="field-hint">walletAuthSecret</code></span>
              <div class="sec-secret-row">
                <span class="pill ${t.walletAuthSecretConfigured ? 'live' : 'warn'}">
                  ${t.walletAuthSecretConfigured ? `${icon('check', { size: 12 })} configured` : `${icon('x', { size: 12 })} not set`}
                </span>
                ${
                  t.walletAuthSecretPrevConfigured
                    ? `<span class="pill warn" title="Previous secret still accepted during overlap window">${icon('warn', { size: 12 })} prev active${
                        t.walletAuthSecretRotatedAt
                          ? ` (rotated ${fmtDateTime(t.walletAuthSecretRotatedAt)})`
                          : ''
                      }</span>
                       <button id="td-discard-prev" class="ghost" style="margin:0">Discard previous</button>`
                    : ''
                }
                <button id="td-rotate-secret" class="ghost" style="margin:0">Rotate secret…</button>
              </div>
            </div>
          </div>
          <div class="row sec-test-row">
            <button id="td-test-ping" class="ghost" ${t.walletBaseUrl && t.walletAuthSecretConfigured ? '' : 'disabled title="Configure walletBaseUrl + secret first"'}>Send test ping</button>
            <span id="td-test-ping-result" class="muted" style="font-size:12px"></span>
          </div>
        </div>

        <div class="card sec-card">
          <div class="sec-card-head">
            <h2>Access control</h2>
            <span class="muted">Restrict who can reach this integrator. Empty list = open.</span>
          </div>
          <div class="sec-grid">
            <div class="field" style="grid-column:1 / -1">
              <span class="field-label">
                Allowed IPs <code class="field-hint">allowedIps</code> · CIDR
                ${t.allowedIps.length === 0 ? `<span class="field-warn">${icon('warn', { size: 12 })} currently open</span>` : ''}
              </span>
              ${chipEditor('td-allowed-ips', t.allowedIps, 'Add an IP / CIDR…')}
            </div>
            <div class="field" style="grid-column:1 / -1">
              <span class="field-label">Allowed origins <code class="field-hint">allowedOrigins</code> · iframe URLs</span>
              ${chipEditor('td-allowed-origins', t.allowedOrigins, 'Add an origin URL…')}
            </div>
          </div>
        </div>

        <div class="card sec-card">
          <div class="sec-card-head">
            <h2>Reconciliation</h2>
            <span class="muted">Allowed balance mismatch before a call is flagged as drift.</span>
          </div>
          <div class="sec-grid">
            <label class="field">
              <span class="field-label">Drift tolerance <code class="field-hint">walletDriftTolerance</code></span>
              <input id="td-drift-tolerance" type="number" min="0" value="${escapeHtml(t.walletDriftTolerance)}" />
            </label>
          </div>
        </div>

        <div class="sec-actions">
          <button id="td-save-security" class="primary">Save changes</button>
          <span class="muted" style="font-size:12px">Saves the endpoint, access control + reconciliation settings.</span>
        </div>
      </div>

      <div data-panel="rates" class="td-panel">
        <div class="card">
          <div class="detail-card-head">
            <h2>Enabled currencies</h2>
            <span class="muted">Currencies this integrator offers, enabled from the platform registry. Disabling stops new sessions in that currency; existing balances are untouched.${deps.can('game.fairness.write') ? ' Manage the platform-wide list on the <a href="/currencies">Currencies</a> page.' : ''}</span>
          </div>
          <div id="td-fiat-list"><p class="muted">Loading…</p></div>
        </div>
      </div>

      <div data-panel="jackpots" class="td-panel">
        <div class="card">
          <div class="detail-card-head">
            <h2>Jackpot pools</h2>
            <span class="muted">Per-integrator, per-game pools. The accrual ledger is the source of truth; balances are read live on every round settle.</span>
          </div>
          <div id="td-jackpot-list"><p class="muted">Loading…</p></div>
        </div>
      </div>

      <div data-panel="autoplay" class="td-panel">
      <div class="card" id="td-autoplay-card">
        <div class="detail-card-head">
          <h2>Auto-play <span class="field-hint">greedyking</span></h2>
          <span class="muted">Advisory caps shown in the game modal. Bets still pass wallet idempotency + balance checks, and carry <code>meta.auto=true</code> for audit.</span>
        </div>
        <div class="sec-grid">
          <label class="field">
            <span class="field-label">Feature enabled</span>
            <select id="td-ap-enabled">
              <option value="true">Enabled</option>
              <option value="false">Disabled</option>
            </select>
          </label>
          <label class="field">
            <span class="field-label">Max rounds per session</span>
            <input id="td-ap-max-rounds" type="number" min="1" max="10000" />
          </label>
          <label class="field">
            <span class="field-label">Max stake per round <span class="muted" style="font-size:11px">(empty = no cap)</span></span>
            <input id="td-ap-max-stake" type="text" placeholder="e.g. 100000" />
          </label>
          <label class="field">
            <span class="field-label">Require ≥1 manual bet first</span>
            <select id="td-ap-require-manual">
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </label>
        </div>
        <div class="sec-actions" style="margin-top:16px">
          <button id="td-ap-save" class="primary">Save auto-play config</button>
        </div>
      </div>
      </div>

      <div data-panel="activity" class="td-panel">
      <div class="card">
        <div class="detail-card-head">
          <h2>Recent rounds <span class="muted" style="font-weight:400">(${d.recentRounds.length})</span></h2>
          <span class="muted">Click a round to open its full trace (bets + wallet movements).</span>
        </div>
        ${
          d.recentRounds.length === 0
            ? '<p class="muted">No rounds yet.</p>'
            : `<div style="max-height:40vh;overflow:auto"><table>
                <thead><tr><th>Started</th><th>Game</th><th>State</th><th>Participants</th><th>Total bet</th><th>Total payout</th><th>Round id</th></tr></thead>
                <tbody>${d.recentRounds
                  .map(
                    (r) => `<tr class="row-nav" data-row-href="${gh('transaction-trace')}?id=${encodeURIComponent(r.id)}" title="Open round trace">
                      <td>${fmtTime(r.startedAt)}</td>
                      <td><code>${escapeHtml(r.gameId)}</code></td>
                      <td><span class="pill ${r.state === 'SETTLED' ? 'live' : ''}">${r.state}</span></td>
                      <td>${r.participantCount}</td>
                      <td>${fmtNumber(r.totalBet)}</td>
                      <td>${fmtNumber(r.totalPayout)}</td>
                      <td><code>${r.id.slice(-8)}</code></td>
                    </tr>`,
                  )
                  .join('')}</tbody>
              </table></div>`
        }
      </div>

      <div class="card">
        <div class="detail-card-head">
          <h2>Recent audit activity <span class="muted" style="font-weight:400">(${d.recentAudits.length})</span></h2>
          <span class="muted">Click an entry to open the full audit log.</span>
        </div>
        ${
          d.recentAudits.length === 0
            ? '<p class="muted">No audit entries.</p>'
            : `<div style="max-height:40vh;overflow:auto"><table>
                <thead><tr><th>When</th><th>Action</th><th>Target</th><th>Actor</th></tr></thead>
                <tbody>${d.recentAudits
                  .map(
                    (a) => `<tr class="row-nav" data-row-href="${gh('audit')}" title="Open audit log">
                      <td>${fmtDateTime(a.createdAt)}</td>
                      <td><span class="pill">${escapeHtml(a.action)}</span></td>
                      <td><span class="muted">${escapeHtml(a.targetType)}</span> <code>${a.targetId.slice(-8)}</code></td>
                      <td><code>${a.actorUserId.slice(-8)}</code></td>
                    </tr>`,
                  )
                  .join('')}</tbody>
              </table></div>`
        }
      </div>
      </div>
    `;
  }

  function activateTab(name: string): void {
    const known = new Set(['overview', 'security', 'rates', 'jackpots', 'autoplay', 'activity']);
    const target = known.has(name) ? name : 'overview';
    bodyEl.querySelectorAll<HTMLElement>('.td-panel').forEach((p) => {
      p.classList.toggle('hidden', p.dataset.panel !== target);
    });
    tabsMount.querySelectorAll<HTMLButtonElement>('.td-tabs .game-tab').forEach((b) => {
      b.classList.toggle('active', b.dataset.tab === target);
    });
    if (target === 'rates') void loadRatesPanel();
    if (target === 'jackpots') void loadJackpotsPanel();
  }

  let jackpotsPanelLoaded = false;
  async function loadJackpotsPanel(): Promise<void> {
    if (jackpotsPanelLoaded) return;
    jackpotsPanelLoaded = true;
    const el = bodyEl.querySelector<HTMLElement>('#td-jackpot-list');
    if (!el) return;
    try {
      const pools = await deps.client.listIntegratorJackpots(deps.integratorId);
      el.innerHTML =
        pools.length === 0
          ? `<p class="muted">No pools accrued yet. Pools are created on the first round-settle with a non-zero house-take after this integrator goes live.</p>`
          : `<table><thead><tr><th>Game</th><th>Pool</th><th>Currency</th><th style="text-align:right">Balance</th><th style="text-align:right">Contrib bps</th><th>Last won</th><th>Updated</th></tr></thead><tbody>${pools
              .map(
                (p) => `<tr class="row-nav" data-row-href="/games/${encodeURIComponent(p.gameId)}/jackpot" title="Open ${escapeHtml(p.gameId)} jackpot config">
                <td><code>${escapeHtml(p.gameId)}</code></td>
                <td><span class="pill ${p.scope === 'global' ? 'live' : ''}">${escapeHtml(p.poolId)} · ${escapeHtml(p.scope)}</span></td>
                <td><code>${escapeHtml(p.currency ?? 'IDR')}</code></td>
                <td style="text-align:right;font-variant-numeric:tabular-nums">${fmtMoney(p.balanceCredits, p.currency ?? 'IDR')}</td>
                <td style="text-align:right;font-variant-numeric:tabular-nums">${p.contributionBps}</td>
                <td><span class="muted">${p.lastWonAt ? fmtDateTime(p.lastWonAt) : '—'}</span></td>
                <td><span class="muted">${fmtDateTime(p.updatedAt)}</span></td>
              </tr>`,
              )
              .join('')}</tbody></table>`;
    } catch (err) {
      el.innerHTML = `<p class="error">${escapeHtml(describe(err))}</p>`;
    }
  }

  // Run an async mutation with a busy button (disabled + relabelled), then
  // reload the panel from the server on success. Errors surface as toasts.
  async function withBusy(
    btn: HTMLButtonElement,
    busyLabel: string,
    fn: () => Promise<void>,
  ): Promise<void> {
    const original = btn.textContent;
    btn.disabled = true;
    btn.textContent = busyLabel;
    try {
      await fn();
    } catch (err) {
      showToast(describe(err), 'error');
    } finally {
      // On success the handler reloads the panel, replacing this button's DOM
      // node — only restore state if the button is still attached.
      if (btn.isConnected) {
        btn.disabled = false;
        btn.textContent = original;
      }
    }
  }

  type FiatRow = {
    fiatCurrency: string;
    enabled: boolean;
  };

  function renderFiatEditor(fiats: FiatRow[], supported: string[]): string {
    const rows = fiats
      .map(
        (f) => `<tr data-fiat="${escapeHtml(f.fiatCurrency)}">
          <td><code>${escapeHtml(f.fiatCurrency)}</code></td>
          <td>
            <select data-f="enabled" style="width:110px">
              <option value="true"${f.enabled ? ' selected' : ''}>enabled</option>
              <option value="false"${f.enabled ? '' : ' selected'}>disabled</option>
            </select>
          </td>
          <td style="text-align:right">
            <button type="button" class="ghost" data-act="save-fiat">Save</button>
          </td>
        </tr>`,
      )
      .join('');
    const table = `<table><thead><tr>
        <th>Currency</th><th>Enabled</th><th></th>
      </tr></thead><tbody>${rows}</tbody></table>`;
    // Add currencies from the platform registry only — no free-form codes.
    const have = new Set(fiats.map((f) => f.fiatCurrency));
    const addable = supported.filter((c) => !have.has(c));
    const addForm =
      addable.length === 0
        ? `<p class="muted" style="margin-top:12px">All supported currencies are already added. Manage the platform list on the <a href="/currencies">Currencies</a> page.</p>`
        : `<div class="row" style="gap:8px;margin-top:12px;flex-wrap:wrap;align-items:flex-end">
        <label style="display:flex;flex-direction:column;gap:4px">
          <span class="muted" style="font-size:11px">Add currency (from platform registry)</span>
          <select id="td-fiat-new-code" style="width:220px">${addable
            .map((c) => `<option value="${escapeHtml(c)}">${escapeHtml(c)}</option>`)
            .join('')}</select>
        </label>
        <button type="button" id="td-fiat-add" class="primary">Add</button>
      </div>`;
    return (
      (fiats.length === 0 ? `<p class="muted">No currencies enabled yet.</p>` : table) +
      addForm
    );
  }

  async function saveFiat(code: string, enabled: boolean): Promise<void> {
    await deps.client.upsertIntegratorFiatCurrency(deps.integratorId, code, { enabled });
    showToast(`Saved ${code}.`, 'success');
    await loadRatesPanel(true);
  }

  // Container-level delegated listeners must bind exactly once per node: a
  // panel reload (loadRatesPanel(true)) only swaps the containers' innerHTML,
  // so re-binding the same node would double-fire saves. A full-page refresh()
  // creates fresh container nodes (unmarked) that correctly rebind. The marker
  // lives on the element so it tracks node identity, not a stale closure flag.
  function bindRatesDelegation(fiatEl: HTMLElement): void {
    if (fiatEl.dataset.delegationBound === '1') return;
    fiatEl.dataset.delegationBound = '1';
    // Inline per-row currency save (event delegation over the currency table).
    fiatEl.addEventListener('click', (e) => {
      const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-act="save-fiat"]');
      if (!btn) return;
      const row = btn.closest<HTMLElement>('tr[data-fiat]');
      if (!row) return;
      const code = row.dataset.fiat!;
      const enabled = row.querySelector<HTMLSelectElement>('[data-f="enabled"]')!.value === 'true';
      void withBusy(btn, 'Saving…', () => saveFiat(code, enabled));
    });
  }

  // Add-form buttons live inside the re-rendered innerHTML (fresh nodes each
  // load), so these are (re)bound on every panel render.
  function bindRatesAddForms(fiatEl: HTMLElement): void {
    fiatEl.querySelector<HTMLButtonElement>('#td-fiat-add')?.addEventListener('click', (e) => {
      const btn = e.currentTarget as HTMLButtonElement;
      const code = (fiatEl.querySelector<HTMLSelectElement>('#td-fiat-new-code')?.value || '').trim();
      if (!code) return;
      void withBusy(btn, 'Adding…', () => saveFiat(code, true));
    });
  }

  let ratesPanelLoaded = false;
  async function loadRatesPanel(force = false): Promise<void> {
    if (ratesPanelLoaded && !force) return;
    ratesPanelLoaded = true;
    const fiatEl = bodyEl.querySelector<HTMLElement>('#td-fiat-list');
    if (!fiatEl) return;
    try {
      // The platform currency registry is provider-only. A tenant may see and
      // manage WHICH of its own currencies are enabled, but listing the
      // platform-wide catalogue 403s — so skip that call rather than fail the
      // whole panel on it. The 'add a currency' picker needs the catalogue, so
      // it simply has nothing to offer a tenant.
      const [fiats, currencies] = await Promise.all([
        deps.client.listIntegratorFiatCurrencies(deps.integratorId),
        deps.can('game.fairness.write') ? deps.client.listCurrencies() : Promise.resolve([]),
      ]);
      const supported = currencies
        .filter((c) => c.enabled)
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((c) => c.code);
      fiatEl.innerHTML = renderFiatEditor(fiats, supported);
      bindRatesDelegation(fiatEl);
      bindRatesAddForms(fiatEl);
    } catch (err) {
      const msg = escapeHtml(describe(err));
      fiatEl.innerHTML = `<p class="error">${msg}</p>`;
    }
  }

  function bindTabs(): void {
    const initial = (location.hash || '#overview').slice(1);
    activateTab(initial);
    tabsMount.querySelectorAll<HTMLButtonElement>('.td-tabs .game-tab').forEach((btn) => {
      btn.addEventListener('click', () => {
        const tab = btn.dataset.tab!;
        history.replaceState({}, '', `${location.pathname}#${tab}`);
        activateTab(tab);
      });
    });
  }

  function bindActions(d: IntegratorDetail): void {
    const t = d.integrator;
    wireChipEditors(bodyEl);
    bodyEl.querySelectorAll<HTMLButtonElement>('[data-copy]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const v = btn.dataset.copy ?? '';
        void navigator.clipboard?.writeText(v).then(
          () => showToast('Copied.', 'success'),
          () => showToast('Copy failed.', 'error'),
        );
      });
    });
    bodyEl.querySelector<HTMLButtonElement>('#td-rename')?.addEventListener('click', async () => {
      const next = await promptModal({
        title: 'Rename integrator',
        defaultValue: t.name,
        confirmLabel: 'Save',
      });
      if (!next || next === t.name) return;
      try {
        await deps.client.updateIntegrator(t.id, { name: next });
        showToast('Renamed.', 'success');
        await refresh();
      } catch (err) {
        showToast(describe(err), 'error');
      }
    });
    bodyEl
      .querySelector<HTMLButtonElement>('#td-toggle-status')
      ?.addEventListener('click', async () => {
        const next = t.status === 'active' ? 'suspended' : 'active';
        if (next === 'suspended') {
          const ok = await confirmModal({
            title: 'Suspend this integrator?',
            body: 'New sessions will be rejected until reactivated. Active rounds finish normally.',
            confirmLabel: 'Suspend',
            danger: true,
          });
          if (!ok) return;
        }
        try {
          await deps.client.updateIntegrator(t.id, { status: next });
          showToast(next === 'active' ? 'Reactivated.' : 'Suspended.', 'success');
          await refresh();
        } catch (err) {
          showToast(describe(err), 'error');
        }
      });

    // Save security + wallet panel.
    bodyEl
      .querySelector<HTMLButtonElement>('#td-save-security')
      ?.addEventListener('click', async () => {
        const walletUrl = (
          bodyEl.querySelector<HTMLInputElement>('#td-wallet-url')!.value || ''
        ).trim();
        const walletTimeout = Number(
          bodyEl.querySelector<HTMLInputElement>('#td-wallet-timeout')!.value,
        );
        const drift = bodyEl.querySelector<HTMLInputElement>('#td-drift-tolerance')!.value.trim();
        try {
          await deps.client.updateIntegrator(t.id, {
            walletBaseUrl: walletUrl.length === 0 ? null : walletUrl,
            walletTimeoutMs: walletTimeout,
            allowedIps: chipValues(bodyEl, 'td-allowed-ips'),
            allowedOrigins: chipValues(bodyEl, 'td-allowed-origins'),
            walletDriftTolerance: drift.length === 0 ? '0' : drift,
          });
          showToast('Security + wallet config saved.', 'success');
          await refresh();
        } catch (err) {
          showToast(describe(err), 'error');
        }
      });

    bodyEl
      .querySelector<HTMLButtonElement>('#td-rotate-secret')
      ?.addEventListener('click', async () => {
        const ok = await confirmModal({
          title: 'Rotate wallet auth secret?',
          body: 'Generates a NEW walletAuthSecret. The previous secret stays valid for ~24h so integrators can swap without downtime.',
          confirmLabel: 'Rotate',
          danger: true,
        });
        if (!ok) return;
        try {
          const res = await deps.client.rotateWalletSecret(t.id);
          showSecretRevealModal(res.plaintext, res.warning);
          await refresh();
        } catch (err) {
          showToast(describe(err), 'error');
        }
      });

    bodyEl
      .querySelector<HTMLButtonElement>('#td-discard-prev')
      ?.addEventListener('click', async () => {
        const ok = await confirmModal({
          title: 'Close the overlap window?',
          body: 'Wallet calls signed with the previous secret will immediately start failing with INVALID_SIGNATURE. Confirm the integrator has finished the swap.',
          confirmLabel: 'Discard previous',
          danger: true,
        });
        if (!ok) return;
        try {
          await deps.client.discardPrevWalletSecret(t.id);
          showToast('Previous secret discarded', 'success');
          await refresh();
        } catch (err) {
          showToast(describe(err), 'error');
        }
      });

    bodyEl
      .querySelector<HTMLButtonElement>('#td-test-ping')
      ?.addEventListener('click', async () => {
        const btn = bodyEl.querySelector<HTMLButtonElement>('#td-test-ping');
        const resEl = bodyEl.querySelector<HTMLElement>('#td-test-ping-result');
        if (!btn || !resEl) return;
        btn.disabled = true;
        const originalLabel = btn.textContent;
        btn.textContent = 'Pinging…';
        resEl.textContent = '';
        resEl.style.color = '';
        try {
          const outcome = await deps.client.testWalletPing(t.id);
          if (outcome.ok) {
            resEl.style.color = 'var(--ok, #4ade80)';
            resEl.textContent = `✓ HTTP ${outcome.status} · ${outcome.latencyMs}ms`;
            if (outcome.bodySnippet) resEl.title = outcome.bodySnippet;
            showToast('Integrator ping succeeded.', 'success');
          } else {
            resEl.style.color = 'var(--warn, #f87171)';
            const statusLabel = outcome.status ?? 'no-response';
            resEl.textContent = `✗ ${statusLabel} · ${outcome.latencyMs}ms · ${outcome.error}`;
            if (outcome.bodySnippet) resEl.title = outcome.bodySnippet;
            showToast(`Integrator ping failed: ${outcome.error}`, 'error');
          }
        } catch (err) {
          resEl.style.color = 'var(--warn, #f87171)';
          resEl.textContent = describe(err);
          showToast(describe(err), 'error');
        } finally {
          btn.disabled = false;
          btn.textContent = originalLabel ?? 'Send test ping';
        }
      });

    // ─────────── Auto-play config card. Lazy-loads on render — empty card
    // visible until the GET resolves; that's fine because the fields are
    // optional + the rest of the page stays interactive.
    //
    // Gated on the same permission as the tab it fills. This fetch used to run
    // for every role: an account without `game.fairness.write` never saw the
    // tab, but still issued the request and got a raw "Auto-play config load
    // failed: FORBIDDEN" toast on a page that was otherwise working. Not
    // requesting what the role cannot be shown is the fix — a 403 the user can
    // do nothing about should never have been surfaced as an error.
    if (deps.can('game.fairness.write')) void (async (): Promise<void> => {
      try {
        const cfg = await deps.client.getAutoPlayConfig(t.id, 'greedyking');
        const enabled = bodyEl.querySelector<HTMLSelectElement>('#td-ap-enabled');
        const maxRounds = bodyEl.querySelector<HTMLInputElement>('#td-ap-max-rounds');
        const maxStake = bodyEl.querySelector<HTMLInputElement>('#td-ap-max-stake');
        const requireManual = bodyEl.querySelector<HTMLSelectElement>('#td-ap-require-manual');
        if (enabled) enabled.value = cfg.enabled ? 'true' : 'false';
        if (maxRounds) maxRounds.value = String(cfg.maxRounds);
        if (maxStake) maxStake.value = cfg.maxStake ?? '';
        if (requireManual) requireManual.value = cfg.requireManualFirst ? 'true' : 'false';
      } catch (err) {
        showToast(`Auto-play config load failed: ${describe(err)}`, 'error');
      }
    })();

    bodyEl.querySelector<HTMLButtonElement>('#td-ap-save')?.addEventListener('click', async () => {
      const enabled = bodyEl.querySelector<HTMLSelectElement>('#td-ap-enabled')!.value === 'true';
      const maxRoundsRaw = bodyEl.querySelector<HTMLInputElement>('#td-ap-max-rounds')!.value;
      const maxStakeRaw = bodyEl.querySelector<HTMLInputElement>('#td-ap-max-stake')!.value.trim();
      const requireManual =
        bodyEl.querySelector<HTMLSelectElement>('#td-ap-require-manual')!.value === 'true';
      const maxRounds = Number(maxRoundsRaw);
      if (!Number.isInteger(maxRounds) || maxRounds < 1 || maxRounds > 10_000) {
        showToast('maxRounds must be 1..10000', 'error');
        return;
      }
      const maxStake = maxStakeRaw.length === 0 ? null : maxStakeRaw;
      if (maxStake !== null && !/^\d+$/.test(maxStake)) {
        showToast('Max stake must be a positive integer or empty', 'error');
        return;
      }
      try {
        await deps.client.updateAutoPlayConfig(t.id, 'greedyking', {
          enabled,
          maxRounds,
          maxStake,
          requireManualFirst: requireManual,
        });
        showToast('Auto-play config saved.', 'success');
      } catch (err) {
        showToast(describe(err), 'error');
      }
    });
  }

  await refresh();
}

/** A removable-tag editor (replaces a one-per-line textarea). Chips hold the
 *  value in data-value; an inline input adds new ones on Enter/comma/blur. */
function chipEditor(id: string, items: ReadonlyArray<string>, placeholder: string): string {
  const chips = items
    .map(
      (v) =>
        `<span class="chip-item" data-value="${escapeHtml(v)}"><code>${escapeHtml(v)}</code><button type="button" class="chip-remove" aria-label="Remove ${escapeHtml(v)}">×</button></span>`,
    )
    .join('');
  return `<div class="chip-input" id="${id}" data-chip-editor>
    ${chips}
    <input type="text" class="chip-add-input" placeholder="${escapeHtml(placeholder)}" />
  </div>`;
}

/** Two-letter monogram for the integrator avatar (e.g. "Shortie Dev" → "SD"). */
function monogramOf(s: string): string {
  const parts = s.trim().split(/[\s._-]+/).filter(Boolean);
  const chars = parts.length >= 2 ? parts[0]![0]! + parts[1]![0]! : s.slice(0, 2);
  return chars.toUpperCase() || '?';
}

function kpiCell(label: string, value: number | string, color?: string, href?: string): string {
  const style = color ? `color:${color};` : '';
  const inner = `
    <div class="muted kpi-cell-label">${label}</div>
    <div class="kpi-cell-value" style="${style}">${value}</div>`;
  return href
    ? `<a class="kpi-cell kpi-cell-link" href="${href}">${inner}<span class="kpi-cell-arrow" aria-hidden="true">→</span></a>`
    : `<div class="kpi-cell">${inner}</div>`;
}

function describe(err: unknown): string {
  if (err instanceof AdminApiError) return `${err.code}: ${err.message}`;
  return err instanceof Error ? err.message : String(err);
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      case "'":
        return '&#39;';
      default:
        return c;
    }
  });
}

/** Collect the values from a chip editor by id. */
function chipValues(root: HTMLElement, id: string): string[] {
  const editor = root.querySelector<HTMLElement>(`#${id}`);
  if (!editor) return [];
  return Array.from(editor.querySelectorAll<HTMLElement>('.chip-item'))
    .map((c) => c.dataset.value ?? '')
    .filter(Boolean);
}

/** Wire add (Enter / comma / blur), backspace-to-delete, and remove buttons
 *  for every chip editor under `root`. */
function wireChipEditors(root: HTMLElement): void {
  root.querySelectorAll<HTMLElement>('[data-chip-editor]').forEach((editor) => {
    const input = editor.querySelector<HTMLInputElement>('.chip-add-input');
    if (!input) return;
    const addChip = (raw: string): void => {
      const v = raw.trim();
      input.value = '';
      if (!v) return;
      const exists = Array.from(editor.querySelectorAll<HTMLElement>('.chip-item')).some(
        (c) => c.dataset.value === v,
      );
      if (exists) return;
      const chip = document.createElement('span');
      chip.className = 'chip-item';
      chip.dataset.value = v;
      const code = document.createElement('code');
      code.textContent = v;
      const rm = document.createElement('button');
      rm.type = 'button';
      rm.className = 'chip-remove';
      rm.setAttribute('aria-label', `Remove ${v}`);
      rm.textContent = '×';
      chip.append(code, rm);
      editor.insertBefore(chip, input);
    };
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ',') {
        e.preventDefault();
        addChip(input.value);
      } else if (e.key === 'Backspace' && input.value === '') {
        editor.querySelector<HTMLElement>('.chip-item:last-of-type')?.remove();
      }
    });
    input.addEventListener('blur', () => addChip(input.value));
    editor.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      if (target.closest('.chip-remove')) {
        target.closest('.chip-item')?.remove();
        return;
      }
      if (target === editor) input.focus();
    });
  });
}

function splitCsv(raw: string): string[] {
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

/**
 * One-time plaintext reveal for `walletAuthSecret` rotation. Displays a centred
 * modal, copy-to-clipboard button, and a confirm button gating dismiss so the
 * admin actually saves the secret before closing.
 */
function showSecretRevealModal(plaintext: string, warning: string): void {
  const backdrop = document.createElement('div');
  Object.assign(backdrop.style, {
    position: 'fixed',
    inset: '0',
    background: 'rgba(0,0,0,0.72)',
    display: 'grid',
    placeItems: 'center',
    zIndex: '2000',
  } as CSSStyleDeclaration);
  backdrop.innerHTML = `
    <div role="dialog" style="background:var(--panel,#15171c);border:1px solid var(--border,#23262d);border-radius:12px;padding:22px 24px;width:min(540px,92vw);box-shadow:0 18px 48px rgba(0,0,0,0.55)">
      <h2 style="margin:0 0 8px;font-size:16px">New wallet auth secret</h2>
      <p style="font-size:13px;color:var(--warn,#f87171);margin:0 0 14px;display:flex;align-items:center;gap:6px">${icon('warn', { size: 14 })} ${escapeHtml(warning)}</p>
      <code id="td-secret-value" style="display:block;background:#0a0b0e;border:1px solid #23262d;border-radius:6px;padding:10px 12px;font-size:12px;font-family:ui-monospace,SF Mono,Menlo,monospace;word-break:break-all;color:#e6e6e6;user-select:all">${escapeHtml(plaintext)}</code>
      <label style="display:flex;align-items:center;gap:8px;margin:14px 0 0;font-size:13px">
        <input id="td-secret-confirm" type="checkbox" /> I have copied this secret somewhere safe.
      </label>
      <div style="display:flex;gap:8px;margin-top:14px;justify-content:flex-end">
        <button id="td-secret-copy" class="ghost">Copy</button>
        <button id="td-secret-close" disabled>Close</button>
      </div>
    </div>
  `;
  document.body.appendChild(backdrop);
  const copyBtn = backdrop.querySelector<HTMLButtonElement>('#td-secret-copy')!;
  const closeBtn = backdrop.querySelector<HTMLButtonElement>('#td-secret-close')!;
  const confirm = backdrop.querySelector<HTMLInputElement>('#td-secret-confirm')!;
  copyBtn.addEventListener('click', async () => {
    await navigator.clipboard.writeText(plaintext).catch(() => undefined);
    copyBtn.textContent = 'Copied';
    setTimeout(() => {
      copyBtn.textContent = 'Copy';
    }, 1500);
  });
  confirm.addEventListener('change', () => {
    closeBtn.disabled = !confirm.checked;
  });
  closeBtn.addEventListener('click', () => backdrop.remove());
}
